import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faGarageCar } from '@fortawesome/pro-regular-svg-icons/faGarageCar';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Row,
  Space,
  Tabs,
  Typography,
} from 'antd';
import { StatusTag } from 'pos-client/components/generic/status-tag/StatusTag.tsx';
import { mapToLocationAddressPayload } from 'pos-client/components/locations/utils/mapLocationAddressPayload.ts';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { locationTabs } from 'pos-client/tabs/locationTabs.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type GeolocationCreateEditModel,
  type LocationCreateEditModel,
  LocationCreateEditSchema,
  type LocationModel,
} from 'pos-shared/models/locationSchema.ts';

import { ConfirmCancelModal } from './ConfirmCancelModal.tsx';
import { useLocationTabItems } from './useLocationTabItems.tsx';
import { type ModeType, useModalType } from './useModalType.tsx';

interface CreateEditLocationModalProps {
  isOpen: boolean;
  hideModal: () => void;
  location?: LocationModel; // pass location for edit mode
}

export const CreateEditLocationModal = ({
  isOpen,
  hideModal,
  location,
}: CreateEditLocationModalProps) => {
  const { t } = useTranslation();
  const { mode, setMode } = useModalType();

  const { useBreakpoint } = Grid;

  const { lg } = useBreakpoint();

  const { showSuccess, showError } = useNotifications();

  const modalMinWidth = lg ? '50em' : '';

  const navigate = useNavigate();

  const trpcContext = trpc.useUtils();

  const [isFirstTabComplete, setIsFirstTabComplete] = useState<boolean>(false);
  const [isFirstTabValid, setIsFirstTabValid] = useState<boolean>(false);
  const [isSecondTabComplete, setIsSecondTabComplete] =
    useState<boolean>(false);
  const [isSecondTabValid, setIsSecondTabValid] = useState<boolean>(false);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const REDIRECT_PATH = '/locations';
  const locationForm = useForm<LocationCreateEditModel>({
    mode: 'all',
    shouldFocusError: true,
    resolver: zodResolver(LocationCreateEditSchema),
  });
  const formValues = locationForm.watch();
  const { country } = formValues.address ?? '';

  useEffect(() => {
    if (location) {
      locationForm.reset({
        id: location.id || '',
        name: location.name,
        code: location.code ?? '',
        contact: {
          emailAddress: location?.contact?.emailAddress,
          telephone: location?.contact?.telephone,
          fax: location?.contact?.fax ?? '',
        },
        address: {
          addressLine1: location?.address?.addressLine1,
          addressLine2: location?.address?.addressLine2,
          addressLine3: location?.address?.addressLine3 ?? '',
          addressLine4: location?.address?.addressLine4,
          state: location?.address?.state,
          country: location?.address?.country,
          postalCode: location?.address?.postalCode,
          geoLocation: location?.address
            ?.geoLocation as GeolocationCreateEditModel,
          timeZone: location?.address?.timeZone ?? '',
        },
        isActive: location?.isActive,
        serviceTypes: location.serviceTypes ?? ['Static'],
        categoryId: location.linkedCategories?.[0]?.id ?? null,
      });

      locationForm.trigger();
    }
  }, [location, isOpen]);

  const { isDirty, isValid, errors, dirtyFields } = locationForm.formState;

  const isTabOneValid =
    isDirty &&
    !errors.name &&
    !errors.code &&
    !errors.contact?.emailAddress &&
    !errors.contact?.telephone;

  const usaSelected =
    locationForm.watch()?.address?.country === 'United States';

  const stateHasValue = Boolean(locationForm.watch()?.address?.state?.length);

  const isStateValid = (usaSelected && stateHasValue) || !usaSelected;

  const isTabTwoValid =
    isDirty &&
    !errors.address?.addressLine1 &&
    !errors.address?.addressLine2 &&
    !errors.address?.addressLine3 &&
    !errors.address?.addressLine4 &&
    isStateValid &&
    !errors.address?.state &&
    !errors.address?.country &&
    !errors.address?.postalCode &&
    !errors.address?.geoLocation?.latitude &&
    !errors.address?.geoLocation?.longitude;

  const isFormComplete = isDirty && isValid && isStateValid;

  const watchFirstTabFields = locationForm.watch([
    'name',
    'code',
    'contact.emailAddress',
    'contact.telephone',
  ]);

  const watchSecondTabFields = locationForm.watch([
    'address.addressLine1',
    'address.addressLine4',
    'address.country',
    'address.postalCode',
    'address.geoLocation.latitude',
    'address.geoLocation.longitude',
  ]);

  const { mutate: createLocation, isLoading: isCreating } =
    trpc.locations.createLocation.useMutation({
      onSuccess: async (data) => {
        await trpcContext.locations.findLocations.invalidate();
        navigate(
          `/locations/${data.serviceLocationId}/${locationTabs.summary.key}`
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'create',
            entity: 'service location',
          })
        );
      },
    });

  const { mutate: updateLocation, isLoading: isEditing } =
    trpc.locations.updateLocation.useMutation({
      onSuccess: async () => {
        await trpcContext.locations.findLocations.invalidate();
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'service location',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'service location',
          })
        );
      },
    });

  const { mutate: deleteLocation, isLoading: isDeleting } =
    trpc.locations.removeServiceLocation.useMutation({
      onSuccess: async () => {
        await trpcContext.locations.findLocations.refetch();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'service location',
          })
        );
        handleHideModal();
        navigate(REDIRECT_PATH);
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'service location',
          })
        );
      },
    });

  const { mutate: updateCrendentials } =
    trpc.serviceLocation.updateEpicoreCredentials.useMutation({
      onSuccess: async () => {
        await trpcContext.serviceLocation.getEpicoreCredentials.invalidate({
          id: location?.id as string,
        });
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'epicor details',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'epicor details',
          })
        );
      },
    });

  useEffect(() => {
    if (location) {
      setMode('edit');
    } else {
      setMode('create');
    }
  }, [location]);

  useEffect(() => {
    setIsFirstTabValid(isTabOneValid);
  }, [isTabOneValid]);

  useEffect(() => {
    setIsSecondTabValid(isTabTwoValid);
  }, [isTabTwoValid]);

  const areFirstTabFieldsComplete =
    Object.values(watchFirstTabFields).every(Boolean);
  const areSecondTabFieldsComplete =
    Object.values(watchSecondTabFields).every(Boolean);

  useEffect(() => {
    setIsFirstTabComplete(areFirstTabFieldsComplete && isFirstTabValid);
  }, [isFirstTabValid, areFirstTabFieldsComplete]);

  useEffect(() => {
    setIsSecondTabComplete(areSecondTabFieldsComplete && isSecondTabValid);
  }, [isSecondTabValid, areSecondTabFieldsComplete]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleFirstTabSubmit = () => {
    setIsFirstTabComplete(isFirstTabValid);
    const val = String(Number(activeTab) + 1);

    handleTabChange(val);
  };

  const handleSecondTabSubmit = () => {
    setIsSecondTabComplete(isSecondTabComplete);
    const val = String(Number(activeTab) + 1);

    handleTabChange(val);
  };
  const handlefourthTabSubmit = () => {
    const val = String(Number(activeTab) + 1);

    handleTabChange(val);
  };

  const handleHideModal = () => {
    hideModal();
    setTimeout(() => {
      resetState();
    }, 100); // allow animation to complete
  };

  const onCancel = () => {
    return isDirty ? setCancelModalOpen(true) : handleHideModal();
  };

  const onCancelConfirm = () => {
    locationForm.reset();
    handleHideModal();
  };

  const onCancelReject = () => {
    setCancelModalOpen(false);
  };

  const onSubmitCreate = (data: LocationCreateEditModel) => {
    const location = {
      ...data,
      address: mapToLocationAddressPayload(data.address),
    };

    createLocation(location as LocationCreateEditModel);
  };

  const onSubmitEdit = (data: LocationCreateEditModel) => {
    const locationEdit = {
      ...location,
      ...data,
      address: mapToLocationAddressPayload(data.address),
    };

    updateLocation(locationEdit as LocationModel);

    const { epicorLogin } = formValues;

    if (dirtyFields?.epicorLogin) {
      updateCrendentials({
        serviceLocationId: location?.id as string,
        username: epicorLogin?.username ?? '',
        password: epicorLogin?.password ?? '',
      });
    }

    handleHideModal();
  };

  const { tabItems, activeTab, setActiveTab } = useLocationTabItems({
    locationForm,
    isFirstTabComplete,
    isSecondTabComplete,
    location,
    selectedCountry: country ?? '',
    mode: mode,
    isOpen: isOpen,
  });

  const resetState = () => {
    setCancelModalOpen(false);
    setIsFirstTabValid(false);
    setIsFirstTabComplete(false);
    setIsSecondTabComplete(false);
    setActiveTab('1');
  };

  const { showModal: confirmDeleteLocation } = useConfirmModal({
    type: 'delete',
    content: t('locations.deleteServiceLocationConfirm'),
    confirmText: t('locations.deleteServiceLocation'),
    confirmProps: {
      ghost: true,
    },
  });

  const handleRemoveLocation = locationForm.handleSubmit(() => {
    if (location?.id) {
      confirmDeleteLocation({
        title: `${t('locations.deleteServiceLocation')} ${location.name}`,
        onConfirm() {
          deleteLocation({ id: location.id });
        },
      });
    } else {
      showError(t('assets.idNotFoundError'));
    }
  });

  const renderFooter = (mode: ModeType) => {
    if (mode === 'create') {
      const button = () => {
        if (activeTab === '1') {
          return (
            <Button
              onClick={handleFirstTabSubmit}
              disabled={!isFirstTabComplete}
              type="primary"
            >
              {t('common.buttons.saveContinue')}
            </Button>
          );
        }
        if (activeTab === '2') {
          return (
            <Button
              onClick={handleSecondTabSubmit}
              disabled={!isSecondTabComplete}
              type="primary"
            >
              {t('common.buttons.saveContinue')}
            </Button>
          );
        }
        if (activeTab === '4') {
          return (
            <Button
              onClick={handlefourthTabSubmit}
              disabled={false}
              type="primary"
            >
              {t('common.buttons.next')}
            </Button>
          );
        }

        return (
          <Button
            onClick={locationForm.handleSubmit(onSubmitCreate)}
            disabled={!isFormComplete}
            loading={isCreating || isEditing}
            type="primary"
          >
            {t('locations.createServiceLocation')}
          </Button>
        );
      };
      return (
        <>
          <Divider />
          <Button onClick={onCancel}>{t('common.buttons.cancel')}</Button>
          {button()}
        </>
      );
    }
    if (mode === 'edit') {
      return (
        <Row>
          <Col>
            <Button danger onClick={handleRemoveLocation} loading={isDeleting}>
              {t('common.buttons.delete')}
            </Button>
          </Col>
          <Col flex="auto"></Col>
          <Flex gap="small" wrap="wrap">
            <Button onClick={onCancel}>{t('common.buttons.cancel')}</Button>

            {activeTab === '4' ? (
              <Button
                onClick={handlefourthTabSubmit}
                disabled={false}
                type="primary"
              >
                {t('common.buttons.next')}
              </Button>
            ) : (
              <Button
                onClick={locationForm.handleSubmit(onSubmitEdit)}
                disabled={!isFormComplete}
                loading={isCreating || isEditing}
                type="primary"
              >
                {t('common.buttons.saveChanges')}
              </Button>
            )}
          </Flex>
        </Row>
      );
    }
    return <></>;
  };

  return (
    <>
      <PosModal
        header={{
          title:
            mode === 'edit' ? (
              location ? (
                <Space size="middle" align="start">
                  <Typography.Title level={4} style={{ marginBottom: 0 }}>
                    {location.name}
                  </Typography.Title>
                  <StatusTag
                    status={location?.isActive ? 'active' : 'inactive'}
                  />
                </Space>
              ) : (
                t('locations.editServiceLocation')
              )
            ) : (
              t('locations.createServiceLocation')
            ),
          hasTabs: true,
          icon: faGarageCar,
        }}
        open={isOpen}
        onCancel={onCancel}
        style={{ minWidth: modalMinWidth }}
        footer={renderFooter(mode)}
      >
        <Form layout="vertical">
          <Tabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </Form>
      </PosModal>
      <ConfirmCancelModal
        isOpen={cancelModalOpen}
        reject={onCancelReject}
        confirm={onCancelConfirm}
      />
    </>
  );
};
